import React, { useState, useRef, useEffect } from 'react';
import {
  getAvailableSmaPeriods,
  getAvailableEmaPeriods,
  isBollingerAvailable,
  isIchimokuAvailable,
  isMacdAvailable,
  isObvAvailable,
  isVwapAvailable
} from '../../utils/granularityMetricConfig';
import { ichimokuPalette } from './ichimokuPalette';
import './IndicatorControls.css';

export interface MovingAverageConfig {
  type: 'SMA' | 'EMA';
  period: number;
  color: string;
  enabled: boolean;
}


interface IndicatorControlsProps {
  movingAverages: MovingAverageConfig[];
  onMovingAveragesChange: (configs: MovingAverageConfig[]) => void;
  macdEnabled: boolean;
  onMacdToggle: (enabled: boolean) => void;
  rsiEnabled: boolean;
  onRsiToggle: (enabled: boolean) => void;
  bollingerEnabled: boolean;
  onBollingerToggle: (enabled: boolean) => void;
  vwapEnabled: boolean;
  onVwapToggle: (enabled: boolean) => void;
  volumeSmaEnabled: boolean;
  onVolumeSmaToggle: (enabled: boolean) => void;
  volumeEmaEnabled: boolean;
  onVolumeEmaToggle: (enabled: boolean) => void;
  obvEnabled: boolean;
  onObvToggle: (enabled: boolean) => void;
  atrEnabled: boolean;
  onAtrToggle: (enabled: boolean) => void;
  adxEnabled: boolean;
  onAdxToggle: (enabled: boolean) => void;
  ichimokuEnabled: boolean;
  onIchimokuToggle: (enabled: boolean) => void;
  granularityMins?: number;
};

/**
 * Indicator controls panel for enabling/disabling and configuring chart indicators.
 * Supports all chart technical indicator toggles for the price pane and sub-panes.
 * Compact dropdown design to save screen space.
 */
const IndicatorControls: React.FC<IndicatorControlsProps> = ({
  movingAverages,
  onMovingAveragesChange,
  macdEnabled,
  onMacdToggle,
  rsiEnabled,
  onRsiToggle,
  bollingerEnabled,
  onBollingerToggle,
  vwapEnabled,
  onVwapToggle,
  volumeSmaEnabled,
  onVolumeSmaToggle,
  volumeEmaEnabled,
  onVolumeEmaToggle,
  obvEnabled,
  onObvToggle,
  atrEnabled,
  onAtrToggle,
  adxEnabled,
  onAdxToggle,
  ichimokuEnabled,
  onIchimokuToggle,
  granularityMins = 60,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get available indicators for current granularity from utility
  const availableSmaPeriods = getAvailableSmaPeriods(granularityMins);
  const availableEmaPeriods = getAvailableEmaPeriods(granularityMins);
  const bollingerAvailable = isBollingerAvailable(granularityMins);
  const vwapAvailable = isVwapAvailable(granularityMins);
  const macdAvailable = isMacdAvailable(granularityMins);
  const obvAvailable = isObvAvailable(granularityMins);
  const ichimokuAvailable = isIchimokuAvailable(granularityMins);

  // Calculate dropdown position based on button location
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 280; // min-width from CSS
      const viewportWidth = window.innerWidth;

      // If dropdown would extend past right edge, align it to the right
      if (buttonRect.left + dropdownWidth > viewportWidth - 16) {
        setDropdownPosition('right');
      } else {
        setDropdownPosition('left');
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (index: number) => {
    const updated = [...movingAverages];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    onMovingAveragesChange(updated);
  };

  const handleReset = () => {
    // Reset to default configuration based on granularity
    const defaults: MovingAverageConfig[] = [];
    const colors = ['#2196F3', '#FF9800', '#9C27B0', '#4CAF50', '#F44336', '#00BCD4', '#E91E63'];
    let colorIndex = 0;

    // Add available SMAs
    availableSmaPeriods.forEach((period) => {
      defaults.push({
        type: 'SMA',
        period,
        color: colors[colorIndex % colors.length],
        enabled: colorIndex < 2, // Enable first two by default
      });
      colorIndex++;
    });

    // Add available EMAs
    availableEmaPeriods.forEach((period) => {
      defaults.push({
        type: 'EMA',
        period,
        color: colors[colorIndex % colors.length],
        enabled: false, // EMAs disabled by default
      });
      colorIndex++;
    });

    onMovingAveragesChange(defaults);
    onMacdToggle(false);
    onRsiToggle(false);
    onBollingerToggle(false);
    onVwapToggle(false);
    onVolumeSmaToggle(false);
    onVolumeEmaToggle(false);
    onObvToggle(false);
    onAtrToggle(false);
    onAdxToggle(false);
    onIchimokuToggle(false);
  };

  // Filter moving averages to only show those available for current granularity
  const availableMovingAverages = movingAverages.filter(ma => {
    if (ma.type === 'SMA') {
      return availableSmaPeriods.includes(ma.period);
    } else if (ma.type === 'EMA') {
      return availableEmaPeriods.includes(ma.period);
    }
    return false;
  });

  // Count active indicators
  const activeCount =
    availableMovingAverages.filter(ma => ma.enabled).length +
    (macdEnabled ? 1 : 0) +
    (rsiEnabled ? 1 : 0) +
    (bollingerEnabled ? 1 : 0) +
    (vwapEnabled ? 1 : 0) +
    (volumeSmaEnabled ? 1 : 0) +
    (volumeEmaEnabled ? 1 : 0) +
    (obvEnabled ? 1 : 0) +
    (atrEnabled ? 1 : 0) +
    (adxEnabled ? 1 : 0) +
    (ichimokuEnabled ? 1 : 0);

  return (
    <div className="indicator-controls-compact" ref={dropdownRef}>
      <button
        ref={buttonRef}
        className="indicator-dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Configure technical indicators"
      >
        <span className="indicator-icon">📊</span>
        <span className="indicator-label">Indicators</span>
        {activeCount > 0 && (
          <span className="indicator-count">{activeCount}</span>
        )}
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div
          className={`indicator-dropdown-menu ${dropdownPosition === 'right' ? 'align-right' : 'align-left'}`}
        >
          <div className="indicator-dropdown-header">
            <span className="dropdown-title">Technical Indicators</span>
            <button className="reset-button-compact" onClick={handleReset}>
              Reset
            </button>
          </div>

          {/* Moving Averages */}
          {availableMovingAverages.length > 0 && (
            <div className="indicator-group">
              <div className="indicator-group-label">Moving Averages</div>
              {availableMovingAverages.map((ma) => {
                const originalIndex = movingAverages.indexOf(ma);
                return (
                  <label key={`${ma.type}-${ma.period}`} className="indicator-option">
                    <input
                      type="checkbox"
                      checked={ma.enabled}
                      onChange={() => handleToggle(originalIndex)}
                    />
                    <span className="indicator-color-dot" style={{ backgroundColor: ma.color }} />
                    <span className="indicator-option-name">
                      {ma.type} ({ma.period})
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Oscillators */}
          <div className="indicator-group">
            <div className="indicator-group-label">Oscillators</div>
            <label className={`indicator-option ${!macdAvailable ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={macdEnabled}
                onChange={(e) => onMacdToggle(e.target.checked)}
                disabled={!macdAvailable}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#00BCD4' }} />
              <span className="indicator-option-name">
                MACD (12, 26, 9)
              </span>
              {!macdAvailable && <span className="unavailable-tag">N/A</span>}
            </label>
            <label className="indicator-option">
              <input
                type="checkbox"
                checked={rsiEnabled}
                onChange={(e) => onRsiToggle(e.target.checked)}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#9C27B0' }} />
              <span className="indicator-option-name">
                RSI (14)
              </span>
            </label>
          </div>

          {/* Volatility Bands */}
          <div className="indicator-group">
            <div className="indicator-group-label">Volatility</div>
            <label className={`indicator-option ${!bollingerAvailable ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={bollingerEnabled}
                onChange={(e) => onBollingerToggle(e.target.checked)}
                disabled={!bollingerAvailable}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#00D9FF' }} />
              <span className="indicator-option-name">
                Bollinger Bands (20, 2σ)
              </span>
              {!bollingerAvailable && <span className="unavailable-tag">N/A</span>}
            </label>
            <label className={`indicator-option ${!vwapAvailable ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={vwapEnabled}
                onChange={(e) => onVwapToggle(e.target.checked)}
                disabled={!vwapAvailable}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#5b9bd5' }} />
              <span className="indicator-option-name">
                VWAP (20, ±1σ)
              </span>
              {!vwapAvailable && <span className="unavailable-tag">N/A</span>}
            </label>
            <label className="indicator-option">
              <input
                type="checkbox"
                checked={atrEnabled}
                onChange={(e) => onAtrToggle(e.target.checked)}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#FF6B35' }} />
              <span className="indicator-option-name">
                ATR (14)
              </span>
            </label>
            <label className="indicator-option">
              <input
                type="checkbox"
                checked={adxEnabled}
                onChange={(e) => onAdxToggle(e.target.checked)}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#2196F3' }} />
              <span className="indicator-option-name">
                ADX (14)
              </span>
            </label>
          </div>

          {/* Trend */}
          <div className="indicator-group">
            <div className="indicator-group-label">Trend</div>
            <label className={`indicator-option ${!ichimokuAvailable ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={ichimokuEnabled}
                onChange={(e) => onIchimokuToggle(e.target.checked)}
                disabled={!ichimokuAvailable}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: ichimokuPalette.bullishCloud }} />
              <span className="indicator-option-name">
                Ichimoku Cloud
              </span>
              {!ichimokuAvailable && <span className="unavailable-tag">N/A</span>}
            </label>
          </div>

          {/* Volume Indicators */}
          <div className="indicator-group">
            <div className="indicator-group-label">Volume</div>
            <label className="indicator-option">
              <input
                type="checkbox"
                checked={volumeSmaEnabled}
                onChange={(e) => onVolumeSmaToggle(e.target.checked)}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#4CAF50' }} />
              <span className="indicator-option-name">
                Volume SMA (20)
              </span>
            </label>
            <label className="indicator-option">
              <input
                type="checkbox"
                checked={volumeEmaEnabled}
                onChange={(e) => onVolumeEmaToggle(e.target.checked)}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#FF9800' }} />
              <span className="indicator-option-name">
                Volume EMA (20)
              </span>
            </label>
            <label className={`indicator-option ${!obvAvailable ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={obvEnabled}
                onChange={(e) => onObvToggle(e.target.checked)}
                disabled={!obvAvailable}
              />
              <span className="indicator-color-dot" style={{ backgroundColor: '#2196F3' }} />
              <span className="indicator-option-name">
                OBV (On-Balance Volume)
              </span>
              {!obvAvailable && <span className="unavailable-tag">N/A</span>}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndicatorControls;
